<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <!-- Header -->
      <div class="text-center">
        <img 
          src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEjWba9cb_pkejMGMH7qatIVTvTdJVvF1NZr87pE6PCGXleLLQ1C-68SwWp72ufKhhOXcn0aQthUOqxtQ6Fqn67XnrX2UeWRP85AyKc5nwEKdbAwEQaUTgB4KsXj5iAJqN_LjWNa8G2nFy4tmnz-p2dRlzjbQHqqm_ZL0wEcFSyMYazXkLAF65GcY2wm2VU/s0/_logo-1741262832124.png" 
          alt="Selectair Logo" 
          class="mx-auto mb-12"
        />
        <h2 class="text-3xl font-bold text-gray-900">
          {{ step === 1 ? 'Reset Password' : 'Enter Reset Code' }}
        </h2>
        <p class="mt-2 text-sm text-gray-600">
          {{ step === 1 ? 'Enter your email to receive a reset code' : 'We sent a 6-digit code to your email' }}
        </p>
      </div>

      <!-- Step 1: Email Input -->
      <form v-if="step === 1" class="mt-8 space-y-6" @submit.prevent="requestResetCode">
        <div class="rounded-md shadow-sm -space-y-px">
          <div>
            <label for="email" class="sr-only">Email address</label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"></path>
                </svg>
              </div>
              <input
                id="email"
                v-model="email"
                name="email"
                type="email"
                autocomplete="email"
                required
                class="appearance-none rounded-lg relative block w-full pl-10 pr-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                placeholder="Enter your email address"
                :disabled="loading"
              />
            </div>
          </div>
        </div>

        <!-- Alert Message -->
        <div v-if="showAlert" class="rounded-md p-4" :class="alertClass">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg v-if="alertType === 'success'" class="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
              </svg>
              <svg v-else class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium" :class="alertTextClass">
                {{ alertMessage }}
              </p>
            </div>
          </div>
        </div>

        <div>
          <button
            type="submit"
            :disabled="loading"
            class="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="loading" class="absolute left-0 inset-y-0 flex items-center pl-3">
              <svg class="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
            {{ loading ? 'Sending...' : 'Send Reset Code' }}
          </button>
        </div>

        <div class="text-center">
          <router-link to="/admin/login" class="font-medium text-red-600 hover:text-red-500">
            ← Back to login
          </router-link>
        </div>
      </form>

      <!-- Step 2: Code and Password Input -->
      <form v-if="step === 2" class="mt-8 space-y-6" @submit.prevent="resetPassword">
        <div class="space-y-4">
          <!-- Email Display -->
          <div class="text-center bg-gray-100 rounded-lg p-3">
            <p class="text-sm text-gray-600">Reset code sent to:</p>
            <p class="font-semibold text-gray-900">{{ email }}</p>
          </div>

          <!-- 6-Digit Code Input -->
          <div>
            <label for="code" class="block text-sm font-medium text-gray-700 mb-2">
              6-Digit Reset Code
            </label>
            <input
              id="code"
              v-model="resetCode"
              type="text"
              maxlength="6"
              pattern="[0-9]{6}"
              required
              class="appearance-none rounded-lg relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-red-500 focus:border-red-500"
              placeholder="000000"
              :disabled="loading"
            />
          </div>

          <!-- New Password -->
          <div>
            <label for="new-password" class="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <div class="relative">
              <input
                id="new-password"
                v-model="newPassword"
                :type="showPassword ? 'text' : 'password'"
                required
                minlength="8"
                class="appearance-none rounded-lg relative block w-full px-3 py-3 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-red-500 focus:border-red-500"
                placeholder="Enter new password (min 8 characters)"
                :disabled="loading"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <svg v-if="showPassword" class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                </svg>
                <svg v-else class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path>
                </svg>
              </button>
            </div>
            <p class="mt-1 text-xs text-gray-500">Must be at least 8 characters</p>
          </div>

          <!-- Confirm Password -->
          <div>
            <label for="confirm-password" class="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password
            </label>
            <input
              id="confirm-password"
              v-model="confirmPassword"
              :type="showPassword ? 'text' : 'password'"
              required
              class="appearance-none rounded-lg relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-red-500 focus:border-red-500"
              placeholder="Confirm your new password"
              :disabled="loading"
            />
          </div>
        </div>

        <!-- Alert Message -->
        <div v-if="showAlert" class="rounded-md p-4" :class="alertClass">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg v-if="alertType === 'success'" class="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
              </svg>
              <svg v-else class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium" :class="alertTextClass">
                {{ alertMessage }}
              </p>
            </div>
          </div>
        </div>

        <div class="space-y-3">
          <button
            type="submit"
            :disabled="loading"
            class="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="loading" class="absolute left-0 inset-y-0 flex items-center pl-3">
              <svg class="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
            {{ loading ? 'Resetting...' : 'Reset Password' }}
          </button>

          <!-- Resend Code -->
          <div class="text-center">
            <button
              type="button"
              @click="resendCode"
              :disabled="loading || resendCooldown > 0"
              class="text-sm font-medium text-red-600 hover:text-red-500 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              {{ resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Didn't receive code? Resend" }}
            </button>
          </div>

          <div class="text-center">
            <button
              type="button"
              @click="step = 1"
              class="text-sm font-medium text-gray-600 hover:text-gray-500"
            >
              ← Change email
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AdminForgotPassword',
  data() {
    return {
      step: 1, // 1 = email input, 2 = code and password input
      email: '',
      resetCode: '',
      newPassword: '',
      confirmPassword: '',
      loading: false,
      showAlert: false,
      alertMessage: '',
      alertType: 'error',
      showPassword: false,
      resendCooldown: 0,
      resendInterval: null,
      hasAccess: false,
      currentUser: null,
      currentSelectedBlogger: null,
    };
  },
  computed: {
    alertClass() {
      return this.alertType === 'success'
        ? 'bg-green-50 border border-green-200'
        : 'bg-red-50 border border-red-200';
    },
    alertTextClass() {
      return this.alertType === 'success'
        ? 'text-green-800'
        : 'text-red-800';
    },
  },
  beforeDestroy() {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
    }
  },
  methods: {
    async requestResetCode() {
      this.loading = true;
      this.showAlert = false;

      try {
        const response = await axios.post(
          'https://www.uptodateconnect.com/api/v1/user/forgot-password',
          {
            email: this.email.trim().toLowerCase(),
            source: 'app',
          }
        );

        if (response.data.success) {
          this.alertType = 'success';
          this.alertMessage = 'Reset code sent! Check your email.';
          this.showAlert = true;

          // Move to step 2 after showing success message
          setTimeout(() => {
            this.step = 2;
            this.showAlert = false;
            this.startResendCooldown();
          }, 1500);
        } else {
          this.alertType = 'error';
          // Use the provided error message/payload from API
          this.alertMessage = response.data.message || response.data.payload || 'Something went wrong. Please try again.';
          this.showAlert = true;
        }
      } catch (error) {
        console.error('Request reset code error:', error);
        this.alertType = 'error';
        this.alertMessage = error.response?.data?.message || 'Network error. Please try again.';
        this.showAlert = true;
      } finally {
        this.loading = false;
      }
    },

    async resetPassword() {
      // Validate inputs
      if (this.newPassword.length < 8) {
        this.alertType = 'error';
        this.alertMessage = 'Password must be at least 8 characters.';
        this.showAlert = true;
        return;
      }

      if (this.newPassword !== this.confirmPassword) {
        this.alertType = 'error';
        this.alertMessage = 'Passwords do not match.';
        this.showAlert = true;
        return;
      }

      if (this.resetCode.length !== 6) {
        this.alertType = 'error';
        this.alertMessage = 'Please enter the 6-digit code.';
        this.showAlert = true;
        return;
      }

      this.loading = true;
      this.showAlert = false;

      try {
        const response = await axios.post(
          'https://www.uptodateconnect.com/api/v1/user/reset-password',
          {
            password: this.newPassword,
            code: this.resetCode.trim(),
            source: 'app',
          }
        );

        if (response.data.success) {
          this.alertType = 'success';
          this.alertMessage = 'Password reset successfully! Please login with your new password.';
          this.showAlert = true;

          // Redirect to login page after success
          setTimeout(() => {
            this.$router.push('/admin/login');
          }, 2000);
        } else {
          this.alertType = 'error';
          // Use the provided error message/payload from API
          this.alertMessage = response.data.message || response.data.payload || 'Failed to reset password. Please try again.';
          this.showAlert = true;
        }
      } catch (error) {
        console.error('Reset password error:', error);
        this.alertType = 'error';
        this.alertMessage = error.response?.data?.message || 'Network error. Please try again.';
        this.showAlert = true;
      } finally {
        this.loading = false;
      }
    },

    async resendCode() {
      if (this.resendCooldown > 0) return;

      this.loading = true;
      this.showAlert = false;

      try {
        const response = await axios.post(
          'https://www.uptodateconnect.com/api/v1/user/resend-reset-code',
          {
            email: this.email.trim().toLowerCase(),
          }
        );

        if (response.data.success) {
          this.alertType = 'success';
          this.alertMessage = response.data.message || 'New reset code sent to your email!';
          this.showAlert = true;
          this.startResendCooldown();
        } else {
          this.alertType = 'error';
          // Use the provided error message/payload from API
          this.alertMessage = response.data.message || response.data.payload || 'Failed to resend code.';
          this.showAlert = true;
        }
      } catch (error) {
        console.error('Resend code error:', error);
        this.alertType = 'error';
        this.alertMessage = 'Failed to resend code. Please try again.';
        this.showAlert = true;
      } finally {
        this.loading = false;
      }
    },

    startResendCooldown() {
      this.resendCooldown = 60;
      if (this.resendInterval) {
        clearInterval(this.resendInterval);
      }
      this.resendInterval = setInterval(() => {
        this.resendCooldown--;
        if (this.resendCooldown <= 0) {
          clearInterval(this.resendInterval);
        }
      }, 1000);
    },
  },
};
</script>

<style scoped>
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>

